
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useForm } from 'react-hook-form';
import { Clock, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface CRMSettingsProps {
  onSave: (settings: CRMSettingsFormData) => Promise<void>;
  initialSettings?: CRMSettingsFormData;
}

export interface CRMSettingsFormData {
  bulkInviteTime: string;
}

const CRMSettings: React.FC<CRMSettingsProps> = ({ onSave, initialSettings }) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const defaultSettings: CRMSettingsFormData = {
    bulkInviteTime: '15:00', // Default 3:00 PM
  };

  const form = useForm<CRMSettingsFormData>({
    defaultValues: initialSettings || defaultSettings,
  });

  const handleSubmit = async (data: CRMSettingsFormData) => {
    setIsSaving(true);
    try {
      await onSave(data);
      toast({
        title: 'Settings Saved',
        description: 'Your CRM settings have been updated successfully.',
      });
    } catch (error) {
      console.error('Error saving CRM settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save CRM settings. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          CRM Settings
        </CardTitle>
        <CardDescription>
          Configure your CRM behavior and automatic invitation settings
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="bulkInviteTime"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Daily Bulk User Invite Send Time</FormLabel>
                  <FormControl>
                    <Input
                      type="time"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    The system will automatically send invitations to new users at this time daily.
                  </FormDescription>
                </FormItem>
              )}
            />

            <Button type="submit" disabled={isSaving} className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default CRMSettings;
